/** @format */

import React, { FC, useState, useRef, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import {
  Form,
  FormItem,
  Switch,
  ButtonGroup,
  Button,
  Drawer,
  Select,
  Option,
  Input,
  Icon,
  Message,
  Tooltip
} from '@king-design/react'
import { TablePage } from 'src/components'
import { AnalysisFormValues, AnalysisConfig, MetricObject } from './constants'
import HistoryDrawer from 'src/containers/MonitorCloud/CloudAlarm/historyDrawer'
import CloudAlarm from 'src/containers/MonitorCloud/CloudAlarm'
import { cloneDeep } from 'lodash'
import variable from 'src/static/styles/var'
import { useAsync } from 'react-use'
import Api from 'src/api/ApiCloud'
import dayjs from 'dayjs'
import { request } from 'src/utils'

interface LinkAnalysisProps {
  className?: string
  values: AnalysisFormValues
  updateGraph: () => void
  setValues: (values: AnalysisFormValues) => void
  analysisConfig: AnalysisConfig[]
  setAnalysisConfig: (values: AnalysisConfig[]) => void
  metricList: MetricObject[]
  lastSyncAt: string
  dataSource: string
}

const validateMatchValue = (
  curValue: string | null,
  compareValues: string[] | null[],
  compare: '>' | '<' | '='
): boolean | string => {
  const every = compareValues.every(value => {
    if (!value) return true
    if (!/^(-?\d+)(\.?\d+)?$/.test(value)) return false
    if (compare === '>') {
      return parseFloat(value) > parseFloat(curValue)
    } else if (compare === '<') {
      return parseFloat(value) < parseFloat(curValue)
    } else {
      return parseFloat(value) !== parseFloat(curValue)
    }
  })
  return every || '请设置合理阶梯阈值'
}

let timer = null
const LinkAnalysis: FC<LinkAnalysisProps> = ({
  className,
  values,
  setValues,
  analysisConfig,
  setAnalysisConfig,
  metricList,
  lastSyncAt,
  updateGraph,
  dataSource
}) => {
  const [visible, setVisible] = useState<boolean>(false)
  const [addMetric, setAddMetric] = useState<string>('')
  const formRef = useRef(null)
  const [macthList, setMacthList] = useState<AnalysisConfig[]>([])
  const [updateDiff, setUpdateDiff] = useState<number>(0)

  // 获取链路分析配置项
  useAsync(async () => {
    const result = await request(Api.getUFMAnalysisConfig, {})
    const map =
      result?.data?.DescribeLinkAnalysisResult?.Data?.map(item => {
        const { Normal, Warn, Error, Serious } = item
        return {
          ...item,
          Normal: Normal >= 0 ? Normal + '' : '',
          Warn: Warn >= 0 ? Warn + '' : '',
          Error: Error >= 0 ? Error + '' : '',
          Serious: Serious >= 0 ? Serious + '' : ''
        }
      }) || []
    setAnalysisConfig(map)
  }, [])

  // 修改匹配项
  const changeMatch = (index, value) => {
    const update = cloneDeep(macthList)
    update[index] = { ...update[index], ...value }
    setMacthList(update)
  }

  // 保存配置项
  const submitMatch = async () => {
    const valresult = await formRef.current.validate()
    if (valresult) {
      const params = macthList.map(item => {
        const { Normal, Warn, Error, Serious } = item
        return {
          ...item,
          Normal: Normal ? parseFloat(Normal) : -1,
          Warn: Warn ? parseFloat(Warn) : -1,
          Error: Error ? parseFloat(Error) : -1,
          Serious: Serious ? parseFloat(Serious) : -1
        }
      })
      const submitResult = await request(Api.setUFMAnalysisConfig, {
        method: 'POST',
        data: {
          Metrics: params
        }
      })
      if (submitResult?.data?.PutLinkAnalysisResult?.Data) {
        Message.success('配置成功')
        const map =
          submitResult?.data?.PutLinkAnalysisResult?.Data?.map(item => {
            const { Normal, Warn, Error, Serious } = item
            return {
              ...item,
              Normal: Normal >= 0 ? Normal + '' : '',
              Warn: Warn >= 0 ? Warn + '' : '',
              Error: Error >= 0 ? Error + '' : '',
              Serious: Serious >= 0 ? Serious + '' : ''
            }
          }) || []
        setAnalysisConfig(map)
        setVisible(false)
      }
    }
  }

  // 删除配置项
  const deleteConfig = index => {
    const update = cloneDeep(macthList)
    update.splice(index, 1)
    setMacthList(update)
  }

  const scheme = {
    Metric: {
      title: '监控项',
      template({ Metric }) {
        const find = metricList?.find(item => item.Metric === Metric)
        return (
          <Tooltip content={find.MetricDesc || ''}>
            <span>{Metric.replace(/_/g, ' ')}</span>
          </Tooltip>
        )
      }
    },
    Compare: {
      title: '比较方式',
      width: 100,
      template({ Compare }, index) {
        return (
          <FormItem
            hideLabel
            value={Compare}
            rules={{
              required: true
            }}
          >
            <Select
              width={80}
              value={Compare}
              onChangeValue={value => {
                changeMatch(index, { Compare: value })
              }}
            >
              <Option value=">">{'>'}</Option>
              <Option value="<">{'<'}</Option>
              <Option value="=">{'='}</Option>
            </Select>
          </FormItem>
        )
      }
    },
    Normal: {
      title: '正常',
      template({ Compare, Metric, Normal }, index) {
        const unit = metricList.find(item => item.Metric === Metric)?.Units || ''
        return (
          <FormItem
            hideLabel
            value={Normal}
            rules={{
              isNumber: true,
              unique: value => {
                const match = cloneDeep(macthList[index])
                const { Warn, Error, Serious } = match
                return validateMatchValue(value, [Warn, Error, Serious], Compare)
              }
            }}
          >
            <Input
              className="color_item_normal"
              value={Normal || ''}
              placeholder={unit}
              slotPrefix=" "
              width={120}
              onChangeValue={value => {
                changeMatch(index, { Normal: value })
              }}
            ></Input>
          </FormItem>
        )
      }
    },
    Warn: {
      title: '警告',
      template({ Compare, Metric, Warn }, index) {
        const unit = metricList.find(item => item.Metric === Metric)?.Units || ''
        return (
          <FormItem
            hideLabel
            value={Warn}
            rules={{
              isNumber: true,
              unique: value => {
                const match = cloneDeep(macthList[index])
                const { Error, Serious } = match
                return validateMatchValue(value, [Error, Serious], Compare)
              }
            }}
          >
            <Input
              className="color_item_warn"
              value={Warn || ''}
              placeholder={unit}
              slotPrefix=" "
              width={120}
              onChangeValue={value => {
                changeMatch(index, { Warn: value })
              }}
            ></Input>
          </FormItem>
        )
      }
    },
    Error: {
      title: '错误',
      template({ Compare, Metric, Error }, index) {
        const unit = metricList.find(item => item.Metric === Metric)?.Units || ''
        return (
          <FormItem
            hideLabel
            value={Error}
            rules={{
              isNumber: true,
              unique: value => {
                const match = cloneDeep(macthList[index])
                const { Serious } = match
                return validateMatchValue(value, [Serious], Compare)
              }
            }}
          >
            <Input
              className="color_item_error"
              value={Error || ''}
              placeholder={unit}
              slotPrefix=" "
              width={120}
              onChangeValue={value => {
                changeMatch(index, { Error: value })
              }}
            ></Input>
          </FormItem>
        )
      }
    },
    Serious: {
      title: '严重',
      template({ Metric, Serious }, index) {
        const unit = metricList.find(item => item.Metric === Metric)?.Units || ''
        return (
          <FormItem
            hideLabel
            value={Serious}
            rules={{
              isNumber: true
            }}
          >
            <Input
              className="color_item_serious"
              value={Serious || ''}
              placeholder={unit}
              slotPrefix=" "
              width={120}
              onChangeValue={value => {
                changeMatch(index, { Serious: value })
              }}
            ></Input>
          </FormItem>
        )
      }
    },
    action: {
      title: '操作',
      width: 60,
      template(_, index) {
        return <Icon className="ion-trash-a" onClick={() => deleteConfig(index)} />
      }
    }
  }

  // 计算数据更新时间
  useEffect(() => {
    if (lastSyncAt) {
      const update = () => {
        const cur = dayjs()
        const diff = cur.diff(dayjs(lastSyncAt), 'seconds')
        setUpdateDiff(diff)
      }
      update()
      if (timer) {
        clearInterval(timer)
      }
      timer = setInterval(() => {
        update()
      }, 60 * 1000)
    }
    return () => {
      timer && clearInterval(timer)
    }
  }, [lastSyncAt])

  // 持续时间=>HH:mm
  const durationFromat = useMemo(() => {
    if (updateDiff && updateDiff > 5 * 60) {
      const hours = Math.floor(updateDiff / 3600)
      const minutes = Math.floor((updateDiff % 3600) / 60)
      return `${hours > 0 ? `${hours}小时` : ''}${minutes}分钟`
    } else {
      return ''
    }
  }, [updateDiff])

  return (
    <div className={className}>
      <div className="form_content">
        <div className="left_form">
          <div className="form_item">
            <span className="form_item_label">布局</span>
            <Select
              width={150}
              value={values.layout}
              onChangeValue={value => {
                setValues({
                  ...values,
                  layout: value
                })
              }}
            >
              <Option value="level">分层布局</Option>
              <Option value="disperse">分散布局</Option>
            </Select>
          </div>
          <div className="form_item form_item_switch">
            <span className="form_item_label">链路分析</span>
            <Switch
              on="开启"
              off="关闭"
              width="62"
              height="24"
              value={values.analysisSwitch}
              onChangeValue={value => {
                setValues({
                  ...values,
                  analysisSwitch: value
                })
              }}
            />
            {values.analysisSwitch && (
              <Button
                onClick={() => {
                  setMacthList(analysisConfig)
                  setVisible(true)
                }}
              >
                分析设置
              </Button>
            )}
          </div>
          <div className="form_item form_item_switch">
            <CloudAlarm network="ib" />
          </div>
          <ButtonGroup
            checkType="radio"
            value={values.showName}
            onChangeValue={value => {
              setValues({
                ...values,
                showName: value
              })
            }}
          >
            <Button value="SystemName">System name</Button>
            <Button value="GUID">GUID</Button>
            <Button value="IP">IP</Button>
          </ButtonGroup>
          {durationFromat ? (
            <p className="last_sync_at">
              当前为<span style={{ color: variable.primaryColor }}>{durationFromat}</span>
              前的数据，最新数据请
              <a
                onClick={() => {
                  updateGraph()
                }}
              >
                刷新
              </a>
              获取
            </p>
          ) : null}
        </div>
        <HistoryDrawer network="ib" dataSource={dataSource} />
      </div>
      <div id="link_analysis_dialog">
        <Drawer
          value={visible}
          width={900}
          container={() => document.getElementById('link_analysis_dialog')}
          ok={() => {
            submitMatch()
          }}
          onChangeValue={show => {
            if (show) {
            } else {
              setVisible(show)
            }
          }}
          title="分析设置"
        >
          <div>
            <div className="metric_select">
              <p>监控项：</p>
              <Select width={500} value={addMetric} onChangeValue={val => setAddMetric(val)}>
                {metricList?.length
                  ? metricList
                      .filter(item => !macthList.find(macth => macth.Metric === item.Metric))
                      .map(item => (
                        <Option key={item?.Metric} value={item?.Metric}>
                          {item?.Metric.replace(/_/g, ' ')}
                        </Option>
                      ))
                  : null}
              </Select>
              <Button
                disabled={!addMetric}
                type="primary"
                onClick={() => {
                  if (addMetric) {
                    setMacthList([
                      ...macthList,
                      {
                        Metric: addMetric,
                        Compare: '>',
                        Normal: null,
                        Warn: null,
                        Error: null,
                        Serious: null
                      }
                    ])
                    setAddMetric('')
                  }
                }}
              >
                <Icon className="ion-plus-round" size="small" />
                新建参数
              </Button>
            </div>
            <Form className="matching_color" ref={formRef}>
              <TablePage
                tableProps={{
                  checkType: 'none',
                  data: macthList,
                  scheme: scheme,
                  rowKey: item => {
                    return item.Metric
                  }
                }}
                showPage={false}
              ></TablePage>
            </Form>
          </div>
        </Drawer>
      </div>
    </div>
  )
}

export default styled(LinkAnalysis)`
  height: 48px;
  padding: 8px 0 16px;
  border-bottom: 1px solid #f0f2f4;
  position: relative;
  .form_content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    .left_form {
      display: flex;
      align-items: center;
      .form_item {
        padding-right: 16px;
        margin-right: 16px;
        border-right: 1px solid #f0f2f4;
        .form_item_label {
          padding-right: 10px;
        }
        &.form_item_switch .k-btn {
          margin-left: 16px;
        }
      }
      .last_sync_at {
        margin: 0 0 0 16px;
        line-height: 30px;
      }
    }
  }
  #link_analysis_dialog {
    p {
      margin: 0;
    }
    .metric_select {
      display: flex;
      align-items: center;
      height: 32px;
      .k-btn {
        margin-left: 16px;
      }
    }
    .table_metric {
    }
    .matching_color {
      margin-top: 16px;
      .k-input {
        .k-input-prefix {
          display: block;
          left: 0;
          width: 20px;
          height: 20px;
        }
        &.color_item_normal .k-input-prefix {
          background: #36b42a;
        }
        &.color_item_warn .k-input-prefix {
          background: #ff9a05;
        }
        &.color_item_error .k-input-prefix {
          background: #ff6860;
        }
        &.color_item_serious .k-input-prefix {
          background: #d93021;
        }
      }
      .k-form-item .k-error {
        position: static;
        .c-ellipsis {
          white-space: normal;
        }
      }
    }
    .ion-trash-a {
      cursor: pointer;
      &:hover {
        color: ${variable.primaryColor};
      }
    }
  }
`
